/**
 * PdfTemplateSettings — admin panel for PDF export customisation.
 *
 * Currently: firm logo upload (stored in Supabase Storage "pdf-assets" bucket,
 * URL saved to tenants.pdf_logo_url). Extends naturally to firm name override,
 * address, colour, paper size, etc.
 *
 * Upload flow: browser → signed PUT to Supabase → save public URL via API.
 */

import { useEffect, useRef, useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'

const API = '/api/admin/pdf-settings'

async function call(method, body) {
  const token = await window.__clerkGetToken()
  const res = await fetch(API, {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  const j = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(j.error || 'Request failed: ' + res.status)
  return j
}

export default function PdfTemplateSettings({ dark }) {
  const textPrim  = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut   = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.50)'
  const surface   = dark ? '#0d1e24' : '#ffffff'
  const border    = dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.10)'
  const inputBg   = dark ? 'rgba(255,255,255,0.05)' : '#f4f7f8'

  const [logoUrl,  setLogoUrl]  = useState(null)
  const [busy,     setBusy]     = useState(false)
  const [status,   setStatus]   = useState(null) // { type: 'success'|'error', msg }
  const fileRef = useRef(null)

  useEffect(() => {
    call('GET').then(d => setLogoUrl(d.logoUrl)).catch(() => {})
  }, [])

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true); setStatus(null)
    try {
      // 1. Get signed upload URL
      const { signedUrl, token, path, publicUrl } = await call('POST', {
        action: 'getUploadUrl', filename: file.name, contentType: file.type,
      })
      // 2. PUT directly to Supabase Storage
      const putRes = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type, 'x-upsert': 'true' },
        body: file,
      })
      if (!putRes.ok) throw new Error('Upload failed: ' + putRes.status)
      // 3. Save public URL to tenant settings
      await call('POST', { action: 'saveLogo', logoUrl: publicUrl, logoPath: path })
      setLogoUrl(publicUrl)
      setStatus({ type: 'success', msg: 'Logo saved — new PDFs will use this image.' })
    } catch (err) {
      setStatus({ type: 'error', msg: err.message || 'Upload failed.' })
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleDelete() {
    if (!(await confirmDialog({ title: 'Remove logo?', message: 'New PDFs will use the text mark instead.', confirmLabel: 'Remove logo', danger: true }))) return
    setBusy(true); setStatus(null)
    try {
      await call('POST', { action: 'deleteLogo' })
      setLogoUrl(null)
      setStatus({ type: 'success', msg: 'Logo removed.' })
    } catch (err) {
      setStatus({ type: 'error', msg: err.message || 'Delete failed.' })
    } finally { setBusy(false) }
  }

  const lbl = { fontFamily: '"DM Mono", monospace', fontSize: 10, color: textMut, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }
  const row = { padding: '16px 20px', borderBottom: `0.5px solid ${border}` }
  const note = { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: textMut, marginTop: 6, lineHeight: 1.5 }
  const btn = (variant) => ({
    padding: '7px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12,
    fontWeight: variant === 'primary' ? 600 : 400,
    background: variant === 'primary' ? '#07272D' : variant === 'danger' ? 'none' : inputBg,
    color: variant === 'danger' ? '#c0392b' : variant === 'primary' ? '#fff' : textPrim,
    border: variant === 'danger' ? '0.5px solid #c0392b' : variant === 'primary' ? 'none' : `0.5px solid ${border}`,
    borderRadius: 4, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
  })

  return (
    <div>
      {/* Logo */}
      <div style={row}>
        <span style={lbl}>Firm Logo</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {/* Preview */}
          <div style={{ width: 80, height: 52, background: logoUrl ? 'transparent' : inputBg, border: `0.5px solid ${border}`, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {logoUrl
              ? <img src={logoUrl} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 9, color: textMut }}>No logo</span>
            }
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btn('default')} disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
              </button>
              {logoUrl && (
                <button style={btn('danger')} disabled={busy} onClick={handleDelete}>Remove</button>
              )}
            </div>
            <p style={note}>PNG or JPEG, max 5 MB. Appears top-left on all generated PDFs.<br />Recommended: transparent background, landscape orientation, min 300 × 100 px.</p>
          </div>
        </div>

        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg"
          style={{ display: 'none' }} onChange={handleFile} />

        {status && (
          <div style={{ marginTop: 10, fontFamily: '"DM Sans", sans-serif', fontSize: 12,
            color: status.type === 'success' ? '#1D9E75' : '#c0392b' }}>
            {status.msg}
          </div>
        )}
      </div>

      {/* Placeholder row — firm name override, address, etc. coming later */}
      <div style={{ ...row, borderBottom: 'none' }}>
        <span style={lbl}>More settings</span>
        <p style={note}>Firm name override, address line, and other PDF customisations will appear here.</p>
      </div>
    </div>
  )
}
